import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import DashboardNavBar from "./DashboardNavBar";
import DashboardHero from "./DashboardHero";
import UserWebsites from "./UserWebsites";
import Settings from "./settings/Settings";
import Footer from "../../app/landing/Footer";
import { AuthContext } from "../../core/context/AuthContext";
import { apiFetch } from "../../core/utils/api";
import { Container } from "../../design-system";
import { surfaces } from "../../design-system/tokens";

const Dashboard = () => {
  const { username } = useParams();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { auth, setAuth, logout } = useContext(AuthContext);
  const isCheckingRef = useRef(false);
  const lastCheckTimeRef = useRef(0);

  useEffect(() => {
    // Only re-check session periodically (mount + focus/visibility), not on every click.
    // The old global click handler could cause surprising "snap back" when server session and client state
    // were briefly out of sync during account switching.
    const MIN_INTERVAL_MS = 15 * 1000; // rate-limit checks to avoid spamming the server

    const checkSession = async () => {
      const now = Date.now();
      if (isCheckingRef.current || now - lastCheckTimeRef.current < MIN_INTERVAL_MS) {
        return;
      }
      isCheckingRef.current = true;
      try {
        const res = await apiFetch("/api/auth/profile/");
        if (res.ok) {
          try {
            const data = await res.json();
            if (!auth || auth.userId !== data.id) {
              setAuth({
                userId: data.id,
                username: data.username,
                email: data.email,
                firstName: data.first_name,
                lastName: data.last_name,
              });
            }
          } catch (_) {
            // ignore JSON parse errors
          }
        } else {
          try {
            await logout();
          } finally {
            navigate("/edit/login", { replace: true });
          }
        }
      } catch (_) {
        // Network or other error: treat as unauthenticated for safety
        try {
          await logout();
        } finally {
          navigate("/edit/login", { replace: true });
        }
      } finally {
        lastCheckTimeRef.current = Date.now();
        isCheckingRef.current = false;
      }
    };

    const handleFocus = () => {
      setTimeout(checkSession, 0);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) setTimeout(checkSession, 0);
    };

    // Initial check on mount (helps if session expired while keeping the tab open)
    setTimeout(checkSession, 0);

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [auth, logout, navigate, setAuth]);
  
  // Check if we're on a settings page
  const isSettingsPage = location.pathname.includes("/settings/");
  const dashboardShellClassName = `min-h-screen ${surfaces.modernApp} flex flex-col transition-colors duration-300 relative overflow-x-hidden`;
  
  if (isSettingsPage) {
    return (
      <div className={dashboardShellClassName}>
        <DashboardNavBar username={username} />
        <div className="flex-1 pt-12 sm:pt-16 relative z-10">
          <Settings />
        </div>
        <div className="relative z-10">
          <Footer />
        </div>
      </div>
    );
  }
  
  return (
    <div className={dashboardShellClassName}>
      <DashboardNavBar username={username} />
      <div className="flex-1 pt-12 sm:pt-16 relative z-10">
        <DashboardHero />
        <Container size="7xl" className="py-2 md:py-4">
          <UserWebsites username={username} searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </Container>
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default Dashboard; 